import { create } from 'zustand'
import { persist } from 'zustand/middleware'

interface BookingDraftState {
  draftId: string | null
  currentStep: number
  completedSteps: number[]
  formData: Record<string, Record<string, unknown>>
  /** Maps "fieldKey" → display label chosen by the user (avoids re-fetching remote options in summary) */
  fieldLabels: Record<string, string>

  setDraftId: (draftId: string | null) => void
  setCurrentStep: (step: number) => void
  markStepCompleted: (step: number) => void
  updateStepData: (stepKey: string, data: Record<string, unknown>) => void
  updateFieldLabels: (labels: Record<string, string>) => void
  clearStepsAfter: (stepId: number) => void
  resetStore: () => void
  canNavigateToStep: (step: number) => boolean
}

const initialState = {
  draftId: null as string | null,
  currentStep: 1,
  completedSteps: [] as number[],
  formData: {} as Record<string, Record<string, unknown>>,
  fieldLabels: {} as Record<string, string>
}

export const useStudentBookingStore = create<BookingDraftState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setDraftId: (draftId) => set({ draftId }),

      setCurrentStep: (step) => set({ currentStep: step }),

      markStepCompleted: (step) =>
        set((state) => ({
          completedSteps: Array.from(new Set([...state.completedSteps, step]))
        })),

      updateStepData: (stepKey, data) =>
        set((state) => ({
          formData: {
            ...state.formData,
            [stepKey]: { ...state.formData[stepKey], ...data }
          }
        })),

      updateFieldLabels: (labels) =>
        set((state) => ({
          fieldLabels: { ...state.fieldLabels, ...labels }
        })),

      clearStepsAfter: (stepId) =>
        set((state) => {
          const newFormData = { ...state.formData }
          const keysToRemove = Object.keys(newFormData).filter((key) => {
            const id = parseInt(key.replace('step', ''), 10)
            return !isNaN(id) && id > stepId
          })
          for (const key of keysToRemove) {
            delete newFormData[key]
          }
          return {
            formData: newFormData,
            completedSteps: state.completedSteps.filter((s) => s <= stepId)
          }
        }),

      resetStore: () => set(initialState),

      canNavigateToStep: (step) => {
        const { completedSteps, currentStep } = get()
        return step <= currentStep || completedSteps.includes(step - 1)
      }
    }),
    {
      name: 'camplus-student-booking',
      partialize: (state) => ({
        draftId: state.draftId,
        formData: state.formData,
        completedSteps: state.completedSteps,
        fieldLabels: state.fieldLabels
      })
    }
  )
)
